package link.erpango.quip.data.remote.repository.garage

import io.ktor.http.encodeURLPathPart
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.builtins.serializer
import link.erpango.quip.data.remote.ApiClient
import link.erpango.quip.data.remote.DResponse
import link.erpango.quip.data.remote.repository.IApiRepository
import link.erpango.quip.domain.entities.AcceptTransferResponse
import link.erpango.quip.domain.entities.CancelTransferResponse
import link.erpango.quip.domain.entities.DVehicle
import link.erpango.quip.domain.entities.DVehicleDocument
import link.erpango.quip.domain.entities.DSparePartDocument
import link.erpango.quip.domain.entities.InitiateTransferRequest
import link.erpango.quip.domain.entities.PendingTransferItem
import link.erpango.quip.domain.entities.TransferConflictResponse
import link.erpango.quip.domain.entities.TransferQRResponse
import link.erpango.quip.domain.entities.TransferRecipient
import link.erpango.quip.domain.entities.TransferStatusResponse
import link.erpango.quip.domain.entities.TransferVehicleRequest

class GarageRepository(
    override val api: ApiClient,
): IGarageRepository, IApiRepository<ApiClient> {
    override val resourcePath: String
        get() = "api/vehicles"

    override suspend fun createVehicle(vehicle: DVehicle): DResponse<DVehicle> {
        return this.api.consume("$resourcePath/")
            .post(vehicle, DVehicle.serializer())
            .response(DVehicle.serializer())
    }

    override suspend fun updateVehicle(vin: String, vehicle: DVehicle): DResponse<DVehicle> {
        return this.api.consume("$resourcePath/$vin/")
            .put(vehicle, DVehicle.serializer())
            .response(DVehicle.serializer())
    }

    override suspend fun getMyGarage(): DResponse<List<DVehicle>> {
        return this.api.consume("api/my-vehicles/")
            .get()
            .response(ListSerializer(DVehicle.serializer()))
    }

    override suspend fun getDocuments(vin: String): DResponse<List<DVehicleDocument>> {
        return this.api.consume("$resourcePath/$vin/documents/")
            .get()
            .response(ListSerializer(DVehicleDocument.serializer()))
    }

    override suspend fun getVehicleDocumentsMetadata(vin: String): DResponse<List<DVehicleDocument>> {
        return this.api.consume("$resourcePath/$vin/documents/metadata/")
            .get()
            .response(ListSerializer(DVehicleDocument.serializer()))
    }

    override suspend fun uploadDocument(vin: String, document: DVehicleDocument, base64Content: String): DResponse<DVehicleDocument> {
        val docToSend = document.copy(fileContentBase64 = base64Content)
        return this.api.consume("$resourcePath/$vin/documents/")
            .post(docToSend, DVehicleDocument.serializer())
            .response(DVehicleDocument.serializer())
    }

    override suspend fun deleteDocument(documentId: Long): DResponse<Unit> {
        return this.api.consume("api/documents")
            .delete(documentId.toInt())
            .response(Unit.serializer())
    }

    override suspend fun renameDocument(documentId: Long, name: String): DResponse<DVehicleDocument> {
        return this.api.consume("api/documents")
            .patch(
                documentId.toString(),
                mapOf("name" to name),
                kotlinx.serialization.builtins.MapSerializer(String.serializer(), String.serializer())
            )
            .response(DVehicleDocument.serializer())
    }

    override suspend fun getDocument(documentId: Long): DResponse<DVehicleDocument> {
        return this.api.consume("api/documents/$documentId/")
            .get()
            .response(DVehicleDocument.serializer())
    }

    override suspend fun downloadDocument(documentId: Long): DResponse<DVehicleDocument> {
        return this.api.consume("api/documents/$documentId/")
            .get()
            .response(DVehicleDocument.serializer())
    }

    // Spare Part Documents - TODO: Implement backend integration
    override suspend fun getSparePartDocuments(sparePartId: Long): DResponse<List<DSparePartDocument>> {
        return this.api.consume("api/spare-parts/$sparePartId/documents/")
            .get()
            .response(ListSerializer(DSparePartDocument.serializer()))
    }

    override suspend fun getSparePartDocumentsMetadata(sparePartId: Long): DResponse<List<DSparePartDocument>> {
        return this.api.consume("api/spare-parts/$sparePartId/documents/metadata/")
            .get()
            .response(ListSerializer(DSparePartDocument.serializer()))
    }

    override suspend fun uploadSparePartDocument(document: DSparePartDocument, base64Content: String): DResponse<DSparePartDocument> {
        val docToSend = document.copy(fileContentBase64 = base64Content)
        return this.api.consume("api/spare-parts/${document.sparePartId}/documents/")
            .post(docToSend, DSparePartDocument.serializer())
            .response(DSparePartDocument.serializer())
    }

    override suspend fun getSparePartDocument(documentId: Long): DResponse<DSparePartDocument> {
        return DResponse.Error("Not implemented")
    }

    override suspend fun downloadSparePartDocument(documentId: Long): DResponse<DSparePartDocument> {
        return this.api.consume("api/spare-part-documents/$documentId/")
            .get()
            .response(DSparePartDocument.serializer())
    }

    override suspend fun deleteSparePartDocument(documentId: Long): DResponse<Unit> {
         return this.api.consume("api/spare-part-documents")
             .delete(documentId.toInt())
             .response(Unit.serializer())
    }

    override suspend fun updateLocalSparePartDocumentPath(document: DSparePartDocument, newPath: String) {
        // This is handled by Interactor/DAO, not Repo usually, but interface has it?
        // Actually Repo implementation is usually for REMOTE.
        // The definitions in IGarageRepository seem to mix remote and local or general use cases.
        // For now, these are placeholders.
    }

    override suspend fun syncSparePartDocuments(sparePartId: Long): DResponse<Boolean> {
        return DResponse.Success(true)
    }

    override suspend fun renameSparePartDocument(documentId: Long, newName: String): DResponse<DSparePartDocument> {
         return this.api.consume("api/spare-part-documents")
             .patch(
                 documentId.toString(),
                 mapOf("name" to newName),
                 kotlinx.serialization.builtins.MapSerializer(String.serializer(), String.serializer())
             )
             .response(DSparePartDocument.serializer())
    }

    // Vehicle Transfer - QR/OTP
    
    override suspend fun initiateQrTransfer(vin: String, targetUserId: Int, expiryHours: Int): DResponse<TransferQRResponse> {
        val response = this.api.consume("api/vehicles/$vin/transfer-qr/")
            .post(InitiateTransferRequest(targetUserId, expiryHours), InitiateTransferRequest.serializer())
            .response(TransferQRResponse.serializer())
        // 409: ya hay una transferencia pendiente para el vehículo y el backend la devuelve
        // en `transfer`; se muestra esa en lugar de un error.
        if (response is DResponse.Error && response.statusCode == 409) {
            val conflict = try {
                api.json.decodeFromString(TransferConflictResponse.serializer(), response.body.orEmpty())
            } catch (_: Throwable) {
                null
            }
            conflict?.transfer?.let { return DResponse.Success(it.copy(alreadyActive = true)) }
            return DResponse.Error(conflict?.error ?: response.error, response.throwable, 409, response.body)
        }
        return response
    }

    override suspend fun getActiveQrTransfer(vin: String): DResponse<TransferQRResponse?> {
        val response = this.api.consume("api/vehicles/$vin/transfer-qr/")
            .get()
            .response(TransferQRResponse.serializer())
        return when {
            response is DResponse.Success -> DResponse.Success(response.data.copy(alreadyActive = true))
            // 404: no hay transferencia activa, no es un error para la UI.
            response is DResponse.Error && response.statusCode == 404 -> DResponse.Success(null)
            else -> response
        }
    }

    override suspend fun getTransferRecipient(userId: Int): DResponse<TransferRecipient> {
        val response = this.api.consume("api/transfer/recipient/$userId/")
            .get()
            .response(TransferRecipient.serializer())
        if (response is DResponse.Error) {
            val message = when (response.statusCode) {
                404 -> "No existe ningún usuario con ese ID"
                400 -> response.error?.takeUnless { it.startsWith("Error del servidor") }
                    ?: "No puedes transferirte un vehículo a ti mismo"
                else -> response.error
            }
            return DResponse.Error(message, response.throwable, response.statusCode, response.body)
        }
        return response
    }

    override suspend fun acceptTransfer(token: String): DResponse<AcceptTransferResponse> {
        return this.api.consume("api/transfer/accept/${pathSegment(token)}/")
            .post(Unit, Unit.serializer())
            .response(AcceptTransferResponse.serializer())
    }

    override suspend fun cancelTransfer(token: String): DResponse<CancelTransferResponse> {
        return this.api.consume("api/transfer/cancel/${pathSegment(token)}/")
            .post(Unit, Unit.serializer())
            .response(CancelTransferResponse.serializer())
    }

    override suspend fun getPendingTransfers(): DResponse<List<PendingTransferItem>> {
        return this.api.consume("api/transfer/pending/")
            .get()
            .response(ListSerializer(PendingTransferItem.serializer()))
    }

    override suspend fun checkTransferStatus(token: String): DResponse<TransferStatusResponse> {
        return this.api.consume("api/transfer/status/${pathSegment(token)}/")
            .get()
            .response(TransferStatusResponse.serializer())
    }

    /** Token largo u OTP como segmento de ruta ya codificado (RestConsumer lo concatena tal cual). */
    private fun pathSegment(tokenOrOtp: String): String = tokenOrOtp.trim().encodeURLPathPart()

    // Vehicle Transfer - Direct
    
    override suspend fun initiateDirectTransfer(vin: String, targetUserId: Int, transferIsPrimary: Boolean): DResponse<AcceptTransferResponse> {
        return this.api.consume("api/vehicles/$vin/transfer/")
            .post(TransferVehicleRequest(targetUserId, transferIsPrimary), TransferVehicleRequest.serializer())
            .response(AcceptTransferResponse.serializer())
    }

}
